import {
    getWorkspaceResourceLabel,
    type WorkspaceResourceLabelSource,
} from '@/shared/components/workspace-resources/resourceLabel';
import type { AuditLog, ResourceActivityResourceType } from '@/types';

import { normalizeAuditAction, normalizeAuditResource } from './auditLogFilters';

export type AuditDetails = WorkspaceResourceLabelSource & Record<string, unknown>;

const resourceTypeByLabel: Record<string, ResourceActivityResourceType> = {
    Dashboard: 'dashboards',
    Folder: 'folders',
    'Imported Company': 'importedEntities',
    Investment: 'investments',
    Lifecycle: 'lifecycles',
    List: 'lists',
    'List View': 'listViews',
    Memo: 'memos',
    Pipeline: 'pipelines',
    Report: 'reports',
    'Report Component': 'reportComponents',
    Task: 'tasks',
    Team: 'teams',
};

const resourceRouteRoot: Partial<Record<ResourceActivityResourceType, string>> = {
    dashboards: '/dashboards',
    folders: '/folders',
    investments: '/investments',
    lifecycles: '/lifecycles',
    lists: '/lists',
    memos: '/memos',
    pipelines: '/pipelines',
    teams: '/settings/workspace/permissions/teams',
};

const stringValue = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

const recordValue = (value: unknown): Record<string, unknown> | undefined =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

export const getAuditDetails = (log: AuditLog): AuditDetails => recordValue(log.details) ?? {};

const nestedId = (details: AuditDetails, key: string) => stringValue(recordValue(details[key])?.id);

export const getAuditResourceType = (log: AuditLog): ResourceActivityResourceType | undefined =>
    resourceTypeByLabel[normalizeAuditResource(log.entityType)];

export const getAuditResourceTitle = (log: AuditLog) => {
    const resourceLabel = normalizeAuditResource(log.entityType);

    return getWorkspaceResourceLabel(getAuditDetails(log), resourceLabel);
};

export const getAuditResourceHref = (log: AuditLog, resourceType = getAuditResourceType(log)): string | undefined => {
    if (!log.entityId || !resourceType) {
        return undefined;
    }

    if (resourceType === 'tasks') {
        return `/relations/tasks?task=${encodeURIComponent(log.entityId)}`;
    }

    const details = getAuditDetails(log);

    if (resourceType === 'reports') {
        const dashboardId = nestedId(details, 'dashboard') ?? stringValue(details.dashboardId);

        return dashboardId ? `/dashboards/${dashboardId}?report=${encodeURIComponent(log.entityId)}` : undefined;
    }

    if (resourceType === 'listViews') {
        const listId = nestedId(details, 'list') ?? stringValue(details.listId);

        return listId ? `/lists/${listId}` : undefined;
    }

    const routeRoot = resourceRouteRoot[resourceType];

    return routeRoot ? `${routeRoot}/${log.entityId}` : undefined;
};

export const getAuditActionSummary = (log: AuditLog) => {
    const action = normalizeAuditAction(log.action);
    const description = recordValue(getAuditDetails(log).resourceActivityDescription);
    if (
        ['Update', 'Partial Update'].includes(action) &&
        description?.version === 1 &&
        typeof description.action === 'string' &&
        description.action.length <= 120 &&
        Array.isArray(description.fields) &&
        description.fields.length > 0
    ) {
        return description.action;
    }
    const summaries: Record<string, string> = {
        'Access Denied': 'was denied access to',
        Create: 'created',
        Delete: 'deleted',
        'Partial Update': 'updated',
        Read: 'viewed',
        Update: 'updated',
    };

    return summaries[action] ?? action.toLocaleLowerCase();
};

export const getAuditLogDetailPayload = (log: AuditLog) => ({
    action: normalizeAuditAction(log.action),
    actor: {
        emailAddress: log.user?.emailAddress,
        id: log.user?.id,
        name: `${log.user?.firstName ?? ''} ${log.user?.lastName ?? ''}`.trim() || undefined,
    },
    client: {
        browser: log.browser,
        deviceType: log.deviceType,
        ipAddress: log.ipAddress,
    },
    details: getAuditDetails(log),
    id: log.id,
    member: {
        id: log.member?.id,
        permission: log.member?.permission,
        status: log.member?.status,
    },
    occurredAt: log.createdAt,
    resource: {
        id: log.entityId,
        title: getAuditResourceTitle(log),
        type: normalizeAuditResource(log.entityType),
    },
    updatedAt: log.updatedAt,
});
